import { flatten, range } from 'lodash';
import { keyframes, css } from 'styled-components';

import { isiOS } from './config';
import { FULL_VH, animationFixStyle } from 'components/Common';

import iOSWarningIcon from 'inline-icons/ios-warning-icon.svg';
import androidWarningIcon from 'inline-icons/android-warning-icon.svg';

export const getTheme = () => (isiOS ? 'iOS' : 'material');

export const androidNavbarTopRowHeight = 56;
export const androidNavbarBottomRowHeight = 49;
export const androidNavbarHeight = ({ top = 0 }) => {
  if (top > androidNavbarTopRowHeight) {
    return `${androidNavbarBottomRowHeight}px`;
  }
  return `${androidNavbarTopRowHeight + androidNavbarBottomRowHeight - top}px`;
};

export const androidSlideDuration = 100;
export const androidTransitionDuration = 300;
export const iOSSlideDuration = 100;
export const iOSTransitionDuration = 400;

const iOSPageShadow = `
  position: absolute;
  right: 100%;
  top: 0;
  width: 16px;
  height: 100%;
  background: linear-gradient(
    to right,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0) 10%,
    rgba(0, 0, 0, 0.01) 50%,
    rgba(0, 0, 0, 0.2) 100%
  );
  z-index: -1;
  content: '';
  will-change: opacity;
  contain: strict;
`;

const iOSPulse = keyframes`
  50% { fill: #868686; }
  to { fill: rgba(134, 134, 134, .4) }
`;

const androidDashOffset = 187;
const androidLoadingDuration = '1.4s';

const androidDash = keyframes`
  0% {
    stroke-dashoffset: ${androidDashOffset};
  }
  50% {
    stroke-dashoffset: ${(androidDashOffset / 4).toFixed(2)};
    transform:rotate(135deg);
  }
  100% {
    stroke-dashoffset: ${androidDashOffset};
    transform:rotate(450deg);
  }
`;

const androidRotation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(270deg); }
`;

export const iOSNavbarHeightNumber = 44;
export const iOSNavbarHeight = `${iOSNavbarHeightNumber}px`;

export const iOSButtonRadius = '5px';
export const androidButtonRadius = '2px';

export const iOSEdgeDistance = 20;
export const iOSEdgeDistancePx = `${iOSEdgeDistance}px`;
export const androidEdgeDistance = 16;
export const androidEdgeDistancePx = `${androidEdgeDistance}px`;

export const borderColor = '#e7e7e7';
export const borderColorV2 = 'rgba(17,37,48,0.1)';
export const buttonRadius = isiOS ? iOSButtonRadius : androidButtonRadius;
export const fitbitLightGray = '#F6F6F6';
export const fitbitGray = '#707c83';
export const fitbitGreen = '#01b1ba';
export const fitbitDarkGreen = `#007A81`;
export const fitbitGreenV2 = '#007a81';
export const fitbitPink = '#ef426f';
export const fitbitMidnight = '#112530';
export const fitbitPremiumYellow = '#b7832f';
export const fitbitTextBlack = '#112530';
export const fitbitSectionTitleGray = '#707C83';
export const fitbitTextGray = '#707C83';
export const fitbitSectionBackgroundGray = '#E5E5E5';
export const fontBold = '"Google Sans Flex", sans-serif; font-weight: 700';
export const fontLight = '"Google Sans Flex", sans-serif; font-weight: 300';
export const fontRegular = '"Google Sans Flex", sans-serif';
export const fontSemibold = '"Google Sans Flex", sans-serif; font-weight: 500';
export const imageBorderRadius = isiOS ? '4px' : '5px';
export const warningIcon = isiOS ? iOSWarningIcon : androidWarningIcon;

// Modal
export const modalSectionBorderColor = '#edecf2';
export const modalToggleBgColor = 'none';
export const modalToggleBorderColor = '#aaa';
export const modalToggleCheckedBgColor = fitbitMidnight;
export const modalToggleCheckedColor = '#fff';

export const themes = {
  common: {
    borderColor,
    borderColorV2,
    buttonRadius,
    fitbitGray,
    fitbitGreen,
    fitbitGreenV2,
    fitbitDarkGreen,
    fitbitLightGray,
    fitbitPink,
    fitbitMidnight,
    fitbitPremiumYellow,
    fontBold,
    fontRegular,
    fontSemibold,
    imageBorderRadius,
    modalSectionBorderColor,
    modalToggleBgColor,
    modalToggleBorderColor,
    modalToggleCheckedBgColor,
    modalToggleCheckedColor,
    warningIcon
  },
  iOS: {
    global: `
      html, body {
        font: 18px ${fontRegular};
        width: 100%;
        height: 100%;
        overflow: hidden;
        background-color: var(--sys-color--surface);
      }

      * {
        font-family: ${fontRegular};
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
        -webkit-touch-callout: none;
        user-select: none;
      }

      input, textarea {
        -webkit-touch-callout: text;
        -webkit-user-select: text;
        user-select: text;
      }

      .slide-left, .slide-right, .forward, .back {
        background: #efeff4;

        .transition {
          height: 100vh;
          width: 100vw;
          overflow: hidden;
          position: relative;
          > div {
            background: transparent;
            position: absolute;
            height: 100%;
            left: 0;
            top: 0;
            width: 100%;
          }
        }

        .app-container, .gallery-container, .back-text, .nav-center, .nav-right {
          transition: all ${iOSTransitionDuration}ms;
          ${animationFixStyle}
        }

        .app-container, .gallery-container:before {
          transition: all ${iOSTransitionDuration}ms;
          ${animationFixStyle}
        }

        .back-text, .nav-center, .nav-right {
          display: block;
        }

        .navbar {
          overflow: hidden;
        }
      }

      .v2-top-bar {
        background: #FFFFFF !important;
      }

      .slide-left, .slide-right {
        .app-container, .gallery-container {
          transition: all ${iOSSlideDuration}ms;
          ${animationFixStyle}
        }
      }

      .transition {
        .navbar {
          transition: none;
        }

        .app-container {
          background: #f5f5f5;
        }
      }

      .forward {
        .slide-enter {
          z-index: 65536;

          .app-container, .gallery-container {
            transform: translate3d(100%, 0, 0);

            &:before {
              ${iOSPageShadow};
            }
          }

          .navbar {
            background-color: transparent;
          }

          .back-text, .nav-center, .nav-right {
            opacity: 0;
            transform: translate3d(30vw, 0, 0);
          }

          .back-text-v2, .permissions-title-v2 {
            opacity: 0;
            transform: translate3d(30vw, 0, 0);
            display: none;
          }
        }

        .slide-enter-active {
          .app-container, .gallery-container {
            transform: translate3d(0, 0, 0);
          }

          .back-text, .nav-center, .nav-right {
            opacity: 1;
            transform: translate3d(0, 0, 0);
          }

          .back-text-v2 {
            opacity: 0;
            transform: translate3d(0, 0, 0);
            display: none;
          }
        }

        .slide-leave {
          .back-text, .nav-center, .nav-right {
            opacity: 1;
          }

          .back-text-v2 {
            opacity: 0;
            display: none;
          }
        }

        .slide-leave-active {
          .app-container, .gallery-container {
            transform: translate3d(-20%, 0, 0);
          }

          .back-text, .nav-center, .nav-right {
            opacity: 0;
            transform: translate3d(-20vw, 0, 0);
          }

          .back-text-v2 {
            opacity: 0;
            transform: translate3d(-20vw, 0, 0);
            display: none;
          }
        }
      }

      .back {
        .slide-enter {
          .app-container, .gallery-container {
            transform: translate3d(-20%, 0, 0);
          }

          .back-text, .nav-center, .nav-right {
            opacity: 0;
            transform: translate3d(-20vw, 0, 0);
          }

          .back-text-v2 {
            opacity: 0;
            transform: translate3d(-20vw, 0, 0);
            display: none;
          }
        }

        .slide-enter-active {
          .app-container, .gallery-container {
            transform: translate3d(0, 0, 0);
          }

          .back-text, .nav-center, .nav-right {
            opacity: 1;
            transform: translate3d(0, 0, 0);
          }

          .back-text-v2 {
            opacity: 0;
            transform: translate3d(0, 0, 0);
            display: none;
          }
        }

        .slide-leave {
          z-index: 65536;

          .app-container, .gallery-container {
            &:before {
              ${iOSPageShadow};
            }
          }

          .navbar {
            background-color: transparent;
          }

          .back-text, .nav-center, .nav-right {
            opacity: 1;
            transform: translate3d(30vw, 0, 0);
          }

          .back-text-v2, .permissions-title-v2 {
            opacity: 0;
            transform: translate3d(30vw, 0, 0);
            display: none;
          }
        }

        .slide-leave-active {
          .app-container, .gallery-container {
            transform: translate3d(100%, 0, 0);
          }

          .back-text, .nav-center, .nav-right {
            opacity: 0;
            transform: translate3d(30vw, 0, 0);
          }

          .back-text-v2 {
            opacity: 0;
            transform: translate3d(30vw, 0, 0);
            display: none;
          }
        }
      }

      .slide-left {
        .slide-enter {
          .app-container, .gallery-container {
            transform: translate3d(100%, 0, 0);
          }
        }

        .slide-enter-active, .slide-leave {
          .app-container, .gallery-container {
            transform: translate3d(0, 0, 0);
          }
        }

        .slide-leave-active {
          .app-container, .gallery-container {
            transform: translate3d(-100%, 0, 0);
          }
        }
      }

      .slide-right {
        .slide-enter {
          .app-container, .gallery-container {
            transform: translate3d(-100%, 0, 0);
          }
        }

        .slide-enter-active, .slide-leave {
          .app-container, .gallery-container {
            transform: translate3d(0, 0, 0);
          }
        }

        .slide-leave-active {
          .app-container, .gallery-container {
            transform: translate3d(100%, 0, 0);
          }
        }
      }

    `,
    edgeDistanceRaw: iOSEdgeDistance,
    edgeDistance: iOSEdgeDistancePx,
    backgroundColor: '#f5f5f5',
    marginTop: iOSNavbarHeight,
    AppListBorder: `1px solid ${borderColor}`,
    AppList: ({ borderTop }) => `
      background-color: white;

      ${borderTop ? `border-top: 1px solid ${borderColor};` : ''}
    `,
    AppDigestIcon: `
      height: 52px;
      width: 52px;
    `,
    AppMainContent: `
      border-bottom: 1px solid ${borderColor};
      border-top: 1px solid ${borderColor};
      > a {
        display: block;
      }
      a > div, > div {
        border-top: 1px solid ${borderColor};
        margin: 0;
        padding-left: 0;
      }
      > div:first-of-type {
        border-top: none;
      }
    `,
    Button: `
      border-radius: ${buttonRadius};
      width: 24vw;
    `,
    LoadingIcon: css`
      height: 27px;
      width: 27px;
      fill: #868686;

      path {
        ${flatten(
          range(12).map(
            (value) =>
              css`
                &:nth-of-type(${value + 1}) {
                  animation: ${iOSPulse} 1s ${-0.083 * value}s infinite linear;
                }
              `
          )
        )}
      }
    `,
    Modal: `
      margin-left: -40.67vw;
      width: 81.33vw;
      border-radius: 13px;
    `,
    ModalButtonGroup: `
      border-radius: 0 0 13px 13px;
    `,
    NavbarHeight: iOSNavbarHeight,
    Page: `
      -webkit-overflow-scrolling: touch;
      -webkit-backface-visibility: hidden;
      -webkit-transform-style: preserve-3d;
    `,
    PermissionContainer: `
      padding: 20px 0;
      max-height: ${0.9 * FULL_VH - 150}px;
    `,
    PermissionContent: '',
    PermissionDescription: `
      margin: -10px 0;
    `,
    PermissionList: `
      margin: -10px 0;
    `,
    PermissionLinkWrapper: `
      display: inline-block;
      width: 95%;
    `,
    PermissionWarningIcon: `
      float: right;
      width: 22px;
      height: 20px;
      margin-right: 5px;
    `,
    Swiper: `
      background-color: transparent;
      margin: 0 auto 40px;
      padding-top: 40px;
      img {
        border-radius: ${imageBorderRadius};
      }
    `,
    SwiperV2: `
      background-color: transparent;
      margin: 0 auto;
      padding-top: 40px;
      img {
        border-radius: ${imageBorderRadius};
      }
    `,
    SwiperSlider: `
      background-color: transparent;
    `
  },
  tokens: {
    global: `
      :root {
        --sys-color--white: #ffffff;
        --sys-color--primary: #0b57d0;
        --sys-color--on-primary: #ffffff;
        --sys-color--primary-container: #d3e3fd;
        --sys-color--on-primary-container: #0842a0;
        --sys-color--primary-fixed: #d3e3fd;
        --sys-color--on-primary-fixed: #041e49;
        --sys-color--primary-fixed-dim: #a8c7fa;
        --sys-color--on-primary-fixed-variant: #0842a0;
        --sys-color--secondary: #00639b;
        --sys-color--on-secondary: #ffffff;
        --sys-color--secondary-container: #c2e7ff;
        --sys-color--on-secondary-container: #004a77;
        --sys-color--secondary-fixed: #c2e7ff;
        --sys-color--on-secondary-fixed: #001d35;
        --sys-color--secondary-fixed-dim: #7fcfff;
        --sys-color--on-secondary-fixed-variant: #004a77;
        --sys-color--tertiary: #146c2e;
        --sys-color--on-tertiary: #ffffff;
        --sys-color--tertiary-container: #c4eed0;
        --sys-color--on-tertiary-container: #0f5223;
        --sys-color--tertiary-fixed: #c4eed0;
        --sys-color--on-tertiary-fixed: #072711;
        --sys-color--tertiary-fixed-dim: #6dd58c;
        --sys-color--on-tertiary-fixed-variant: #0f5223;
        --sys-color--error: #b3261e;
        --sys-color--on-error: #ffffff;
        --sys-color--error-container: #f9dedc;
        --sys-color--on-error-container: #8c1d18;
        --sys-color--outline: #747775;
        --sys-color--outline-variant: #c4c7c5;
        --sys-color--surface: #ffffff;
        --sys-color--on-surface: #1f1f1f;
        --sys-color--surface-dim: #d3dbe5;
        --sys-color--surface-bright: #ffffff;
        --sys-color--surface-container-lowest: #ffffff;
        --sys-color--surface-container-low: #f8fafd;
        --sys-color--surface-container: #f0f4f9;
        --sys-color--surface-container-high: #e9eef6;
        --sys-color--surface-container-highest: #dde3ea;
        --sys-color--on-surface-variant: #444746;
        --sys-color--inverse-surface: #303030;
        --sys-color--inverse-on-surface: #f2f2f2;
        --sys-color--inverse-primary: #a8c7fa;
        --sys-color-extended--goldenrod: #9D6F00;
        --sys-color-extended--goldenrod-container: #FFDEA9;
        --sys-color-extended--green-container: #BEEFBB;
        --sys-color--scrim: #000000;
        --sys-color--shadow: #000000;

        --sys-typescale-display-large-font: "Google Sans Flex";
        --sys-typescale-display-large-size: 57px;
        --sys-typescale-display-large-weight: 400;
        --sys-typescale-display-large-weight-emphasized: 500;
        --sys-typescale-display-large-line-height: 64px;
        --sys-typescale-display-large-tracking: 0px;
        --sys-typescale-display-medium-font: "Google Sans Flex";
        --sys-typescale-display-medium-size: 45px;
        --sys-typescale-display-medium-weight: 400;
        --sys-typescale-display-medium-weight-emphasized: 500;
        --sys-typescale-display-medium-line-height: 52px;
        --sys-typescale-display-medium-tracking: 0px;
        --sys-typescale-display-small-font: "Google Sans Flex";
        --sys-typescale-display-small-size: 36px;
        --sys-typescale-display-small-weight: 400;
        --sys-typescale-display-small-weight-emphasized: 500;
        --sys-typescale-display-small-line-height: 44px;
        --sys-typescale-display-small-tracking: 0px;
        --sys-typescale-headline-large-font: "Google Sans Flex";
        --sys-typescale-headline-large-size: 32px;
        --sys-typescale-headline-large-weight: 400;
        --sys-typescale-headline-large-weight-emphasized: 500;
        --sys-typescale-headline-large-line-height: 40px;
        --sys-typescale-headline-large-tracking: 0px;
        --sys-typescale-headline-medium-font: "Google Sans Flex";
        --sys-typescale-headline-medium-size: 28px;
        --sys-typescale-headline-medium-weight: 400;
        --sys-typescale-headline-medium-weight-emphasized: 500;
        --sys-typescale-headline-medium-line-height: 36px;
        --sys-typescale-headline-medium-tracking: 0px;
        --sys-typescale-headline-small-font: "Google Sans Flex";
        --sys-typescale-headline-small-size: 24px;
        --sys-typescale-headline-small-weight: 400;
        --sys-typescale-headline-small-weight-emphasized: 500;
        --sys-typescale-headline-small-line-height: 32px;
        --sys-typescale-headline-small-tracking: 0px;
        --sys-typescale-title-large-font: "Google Sans Flex";
        --sys-typescale-title-large-size: 22px;
        --sys-typescale-title-large-weight: 400;
        --sys-typescale-title-large-weight-emphasized: 500;
        --sys-typescale-title-large-line-height: 28px;
        --sys-typescale-title-large-tracking: 0px;
        --sys-typescale-title-medium-font: "Google Sans Flex";
        --sys-typescale-title-medium-size: 16px;
        --sys-typescale-title-medium-weight: 500;
        --sys-typescale-title-medium-weight-emphasized: 600;
        --sys-typescale-title-medium-line-height: 24px;
        --sys-typescale-title-medium-tracking: 0px;
        --sys-typescale-title-small-font: "Google Sans Flex";
        --sys-typescale-title-small-size: 14px;
        --sys-typescale-title-small-weight: 500;
        --sys-typescale-title-small-weight-emphasized: 600;
        --sys-typescale-title-small-line-height: 20px;
        --sys-typescale-title-small-tracking: 0px;
        --sys-typescale-label-large-font: "Google Sans Flex";
        --sys-typescale-label-large-size: 14px;
        --sys-typescale-label-large-weight: 500;
        --sys-typescale-label-large-weight-emphasized: 600;
        --sys-typescale-label-large-line-height: 20px;
        --sys-typescale-label-large-tracking: 0px;
        --sys-typescale-label-medium-font: "Google Sans Flex";
        --sys-typescale-label-medium-size: 12px;
        --sys-typescale-label-medium-weight: 500;
        --sys-typescale-label-medium-weight-emphasized: 600;
        --sys-typescale-label-medium-line-height: 16px;
        --sys-typescale-label-medium-tracking: 0px;
        --sys-typescale-label-small-font: "Google Sans Flex";
        --sys-typescale-label-small-size: 11px;
        --sys-typescale-label-small-weight: 500;
        --sys-typescale-label-small-weight-emphasized: 600;
        --sys-typescale-label-small-line-height: 16px;
        --sys-typescale-label-small-tracking: 0px;
        --sys-typescale-body-large-font: "Google Sans Flex";
        --sys-typescale-body-large-size: 16px;
        --sys-typescale-body-large-weight: 400;
        --sys-typescale-body-large-weight-emphasized: 500;
        --sys-typescale-body-large-line-height: 24px;
        --sys-typescale-body-large-tracking: 0px;
        --sys-typescale-body-medium-font: "Google Sans Flex";
        --sys-typescale-body-medium-size: 14px;
        --sys-typescale-body-medium-weight: 400;
        --sys-typescale-body-medium-weight-emphasized: 500;
        --sys-typescale-body-medium-line-height: 20px;
        --sys-typescale-body-medium-tracking: 0px;
        --sys-typescale-body-small-font: "Google Sans Flex";
        --sys-typescale-body-small-size: 12px;
        --sys-typescale-body-small-weight: 400;
        --sys-typescale-body-small-weight-emphasized: 500;
        --sys-typescale-body-small-line-height: 16px;
        --sys-typescale-body-small-tracking: 0px;
      }

      @media (prefers-color-scheme: dark) {
        :root {
          --sys-color--primary: #a8c7fa;
          --sys-color--on-primary: #062e6f;
          --sys-color--primary-container: #0842a0;
          --sys-color--on-primary-container: #d3e3fd;
          --sys-color--primary-fixed: #d3e3fd;
          --sys-color--on-primary-fixed: #041e49;
          --sys-color--primary-fixed-dim: #a8c7fa;
          --sys-color--on-primary-fixed-variant: #0842a0;
          --sys-color--secondary: #7fcfff;
          --sys-color--on-secondary: #003355;
          --sys-color--secondary-container: #004a77;
          --sys-color--on-secondary-container: #c2e7ff;
          --sys-color--secondary-fixed: #c2e7ff;
          --sys-color--on-secondary-fixed: #001d35;
          --sys-color--secondary-fixed-dim: #7fcfff;
          --sys-color--on-secondary-fixed-variant: #004a77;
          --sys-color--tertiary: #6dd58c;
          --sys-color--on-tertiary: #0a3818;
          --sys-color--tertiary-container: #0f5223;
          --sys-color--on-tertiary-container: #c4eed0;
          --sys-color--tertiary-fixed: #c4eed0;
          --sys-color--on-tertiary-fixed: #072711;
          --sys-color--tertiary-fixed-dim: #6dd58c;
          --sys-color--on-tertiary-fixed-variant: #0f5223;
          --sys-color--error: #f2b8b5;
          --sys-color--on-error: #601410;
          --sys-color--error-container: #8c1d18;
          --sys-color--on-error-container: #f9dedc;
          --sys-color--outline: #8e918f;
          --sys-color--outline-variant: #444746;
          --sys-color--surface: #131314;
          --sys-color--on-surface: #e3e3e3;
          --sys-color--surface-dim: #131314;
          --sys-color--surface-bright: #37393b;
          --sys-color--surface-container-lowest: #0e0e0e;
          --sys-color--surface-container-low: #1b1b1b;
          --sys-color--surface-container: #1e1f20;
          --sys-color--surface-container-high: #282a2c;
          --sys-color--surface-container-highest: #333537;
          --sys-color--on-surface-variant: #c4c7c5;
          --sys-color--inverse-surface: #e3e3e3;
          --sys-color--inverse-on-surface: #303030;
          --sys-color--inverse-primary: #0b57d0;
          --sys-color--scrim: #000000;
          --sys-color--shadow: #000000;
          --sys-color-extended--goldenrod-container: #5F4100;
          --sys-color-extended--green-container: #00522C;
          --sys-color-extended--goldenrod: #FFD48C;
        }
      }
    `
  },
  material: {
    global: `
      html, body {
        font-size: 18px ${fontRegular};
        width: 100%;
        height: 100%;
        overflow: hidden;
        background-color: var(--sys-color--surface);
      }

      .transition {
        position: relative;
        height: 100vh;
        width: 100vw;
        overflow: hidden;
      }

      .transition > div {
        background: var(--sys-color--surface);
        position: absolute;
        height: 100%;
        left: 0;
        top: 0;
        transition: all ${androidTransitionDuration}ms;
        width: 100%;
      }

      .slide-left, .slide-right {
        background: var(--sys-color--surface);

        .transition > div {
          background: transparent;
        }

        .gallery-container {
          transition: all ${androidSlideDuration}ms;
        }
      }

      .forward {
        .slide-enter {
          opacity: 0;
          transform: translate3d(0, 56px, 0);
        }

        .slide-enter-active {
          opacity: 1;
          transform: translate3d(0, 0, 0);
        }

        .slide-leave {
          opacity: 1;
          z-index: -1;
        }
      }

      .back {
        .slide-enter {
          transform: translate3d(0, 0, 0);
          z-index: -1;
        }

        .slide-leave {
          opacity: 1;
          transform: translate3d(0, 0, 0);
        }

        .slide-leave-active {
          opacity: 0;
          transform: translate3d(0, 56px, 0);
        }
      }

      .slide-left {
        .slide-enter .gallery-container {
          transform: translate3d(100%, 0, 0);
        }

        .slide-enter-active .gallery-container {
          transform: translate3d(0, 0, 0);
        }

        .slide-leave .nav-bar {
          display: none;
        }

        .slide-leave .gallery-container {
          transform: translate3d(0, 0, 0);
        }

        .slide-leave-active .gallery-container {
          transform: translate3d(-100%, 0, 0);
        }
      }

      .slide-right {
        .slide-enter .gallery-container {
          transform: translate3d(-100%, 0, 0);
        }

        .slide-enter-active .gallery-container {
          transform: translate3d(0, 0, 0);
        }

        .slide-leave .nav-bar {
          display: none;
        }

        .slide-leave .gallery-container {
          transform: translate3d(0, 0, 0);
        }

        .slide-leave-active .gallery-container {
          transform: translate3d(100%, 0, 0);
        }
      }

      * {
        font-family: ${fontRegular};
        user-select: none;
      }
    `,
    edgeDistanceRaw: androidEdgeDistance,
    edgeDistance: androidEdgeDistancePx,
    backgroundColor: 'white',
    marginTop: androidNavbarHeight,
    AppList: ({ borderTop }) => {
      const rules = {
        'background-color': 'white'
      };
      if (borderTop) {
        rules['border-top'] = `1px solid ${borderColor}`;
      }
      return rules;
    },
    AppMainContent: `
      > div, a {
        border-bottom: 1px solid ${borderColor};
      },
      > div:first-of-type {
        border: none;
      }
      > a {
        display: block;
      }
    `,
    AppDigestIcon: `
      height: 48px;
      width: 48px;
    `,
    Button: `
      border-radius: ${buttonRadius};
      padding: 5px 0;
      width: 116px;
    `,
    LoadingIcon: css`
      height: 40px;
      width: 40px;
      animation: ${androidRotation} ${androidLoadingDuration} linear infinite;

      circle {
        stroke: #00b0b9;
        stroke-dasharray: ${androidDashOffset};
        stroke-dashoffset: 0;
        transform-origin: center;
        animation: ${androidDash} ${androidLoadingDuration} ease-in-out infinite;
      }
    `,
    Modal: `
      box-shadow: 0 19px 38px rgba(0, 0, 0, .3), 0 15px 12px rgba(0, 0, 0, .22);
      margin-left: -43.35vw;
      width: 86.7vw;
      border-radius: 5px;
    `,
    ModalButtonGroup: '',
    NavbarHeight: androidNavbarHeight,
    PermissionContainer: '',
    PermissionContent: '',
    PermissionDescription: '',
    PermissionList: '',
    PermissionLinkWrapper: '',
    PermissionWarningIcon: `
      float: right;
      width: 22px;
      height: 20px;
    `,
    Swiper: `
      background-color: white;
      margin-bottom: 42px;
      img {
        border-radius: ${imageBorderRadius};
      }
    `,
    SwiperV2: `
      background-color: white;
      margin-bottom: 10px;
      img {
        border-radius: ${imageBorderRadius};
      }
    `,
    SwiperSlider: `
      background-color: white;
    `
  }
};

export default {
  ...themes.common,
  ...themes[getTheme()],
  ...themes.tokens,
  global: themes[getTheme()].global + '\n' + themes.tokens.global
};
